import { useQueries, useQuery } from '@tanstack/react-query';
import { CircleAlert, VideoOff } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { StatusBadge } from '../../components/StatusBadge';
import { ApiError } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { listRecordings, mediaUrl, startPlayback } from '../playback/api';
import { toSegs } from '../playback/ranges';
import { DRIFT_S, mergeSegs, segmentAt, windowEnd, windowsAt, type Rate } from './sync';

interface Props {
  deviceId: string;
  /** The shared clock: recording instant to show now. */
  t: number;
  /** Wall-clock time of the tick, for what is still being recorded. */
  now: number;
  playing: boolean;
  rate: Rate;
  /** Where the replay started: grants are asked once per replay (audited). */
  replayFrom: number;
}

/**
 * One camera of the mosaic window in synchronised replay. It follows the
 * shared clock: loads the segment covering it, seeks when it drifts more than
 * DRIFT_S away, and shows nothing while the camera has no recording there.
 */
export function SyncTile({ deviceId, t, now, playing, rate, replayFrom }: Props) {
  const { request } = useAuth();
  const grant = useQuery({
    queryKey: ['wall-playback', deviceId, replayFrom],
    queryFn: () => startPlayback(request, deviceId, replayFrom, replayFrom + 3_600_000),
    // Tokens last an hour; renew before they expire.
    staleTime: 50 * 60_000,
    refetchInterval: 50 * 60_000,
    retry: (n, err) => !(err instanceof ApiError) && n < 2,
  });
  const windows = windowsAt(t);
  const lists = useQueries({
    queries: windows.map((w) => ({
      queryKey: ['wall-recordings', deviceId, w],
      queryFn: () => listRecordings(request, deviceId, w, windowEnd(w)),
      enabled: grant.isSuccess,
      // The current hour keeps growing: new segments close every minute.
      refetchInterval: windowEnd(w) > now ? 30_000 : (false as const),
    })),
  });
  // At most two hours of one-minute segments: cheap to rebuild on each tick.
  const segs = mergeSegs(lists.map((q) => toSegs(q.data ?? [])));
  const target = segmentAt(segs, t);

  const video = useRef<HTMLVideoElement>(null);
  const token = grant.data?.token;
  // The segment under the clock; a new one loads by changing the source.
  const src = target && token ? mediaUrl(target.seg.id, token) : undefined;

  useEffect(() => {
    const el = video.current;
    if (!target || !el || el.readyState < 1) return;
    const want = target.offsetMs / 1000;
    // Faster speeds drift further between ticks: tolerate proportionally.
    if (Math.abs(el.currentTime - want) > DRIFT_S * rate) el.currentTime = want;
    if (el.playbackRate !== rate) el.playbackRate = rate;
    if (playing && el.paused) void el.play().catch(() => undefined);
    if (!playing && !el.paused) el.pause();
  });

  if (grant.isError) {
    const err = grant.error;
    const text =
      err instanceof ApiError && err.status === 403
        ? 'Sem permissão para ver gravações'
        : err instanceof ApiError && err.status === 503
          ? 'Gravação indisponível no servidor'
          : 'Não foi possível abrir a gravação';
    return <Notice icon={CircleAlert} text={text} />;
  }
  const loading = grant.isPending || lists.some((q) => q.isPending);
  return (
    <div className="relative size-full bg-black">
      {src && (
        <video
          ref={video}
          src={src}
          className="size-full object-contain"
          playsInline
          muted
          preload="auto"
          aria-label="Gravação"
          onLoadedMetadata={(e) => {
            e.currentTarget.currentTime = (target?.offsetMs ?? 0) / 1000;
            e.currentTarget.playbackRate = rate;
            if (playing) void e.currentTarget.play().catch(() => undefined);
          }}
        />
      )}
      {target && (
        <span className="absolute top-2 left-2 rounded-full bg-canvas/85 shadow-sm">
          <StatusBadge tone="warn" pulse={playing}>
            Gravação no servidor
          </StatusBadge>
        </span>
      )}
      {!target &&
        (loading ? (
          <div aria-hidden="true" className="absolute inset-0 animate-pulse bg-surface-raised" />
        ) : (
          <div className="absolute inset-0">
            <Notice icon={VideoOff} text="Sem gravação neste momento" />
          </div>
        ))}
    </div>
  );
}

function Notice({ icon: Icon, text }: { icon: typeof VideoOff; text: string }) {
  return (
    <div className="grid size-full place-items-center bg-canvas px-3 text-center">
      <div className="flex flex-col items-center gap-1.5">
        <Icon aria-hidden="true" className="size-4 text-fg-subtle" strokeWidth={1.75} />
        <p className="text-2xs text-fg-muted">{text}</p>
      </div>
    </div>
  );
}
